import { TOOLS, toolName, type CategoryId, type ToolId } from '@dale/plans';

/**
 * The words each trade uses around its tools (Herramientas): the page titles
 * the nav already uses, the singular and its gender for "Agregar estación" and
 * "Consultorio agregado", and the first package a business of that type would
 * sell. The names themselves live in the plan catalog (@dale/plans).
 */

type Noun = { one: string; feminine: boolean };

const SINGULAR: Readonly<Record<string, Noun>> = {
  Mesas: { one: 'mesa', feminine: true },
  Salas: { one: 'sala', feminine: true },
  Estaciones: { one: 'estación', feminine: true },
  Cabinas: { one: 'cabina', feminine: true },
  Consultorios: { one: 'consultorio', feminine: false },
  Bahías: { one: 'bahía', feminine: true },
  Espacios: { one: 'espacio', feminine: false },
  Paquetes: { one: 'paquete', feminine: false },
  Planes: { one: 'plan', feminine: false },
  Membresías: { one: 'membresía', feminine: true },
};

/** What each trade would sell first, so the empty page shows the idea instead of a blank. */
const FIRST_PACKAGE: Readonly<Record<CategoryId, string>> = {
  restaurant: '5 almuerzos por $40',
  barbershop: '4 cortes por $40',
  salon: '3 blowouts por $60',
  doctor: '4 sesiones por $160',
  florist: 'un arreglo cada semana por $100 al mes',
  autoshop: '3 lavados por $45',
  gym: 'un mes ilimitado por $60',
  nailstudio: '4 manicures por $48',
  spa: '5 masajes por $200',
  classstudio: '10 clases por $120',
  dogwalker: '10 paseos por $100',
  cleaner: '4 limpiezas por $160',
  plumber: '2 visitas de mantenimiento por $90',
  electrician: '2 revisiones por $90',
  handyman: '3 visitas por $120',
  tutor: '8 tutorías por $160',
  trainingcenter: '8 entrenamientos por $96',
};

/** A tool's page title: the name the nav gives it in this trade. */
export function toolTitle(tool: ToolId, category: CategoryId | undefined): string {
  return category ? toolName(tool, category) : TOOLS[tool].otherwise;
}

function noun(title: string): Noun {
  return SINGULAR[title] ?? { one: title.toLowerCase(), feminine: false };
}

function capitalized(word: string): string {
  return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
}

/** Planes, Paquetes or Membresías before the first one exists. */
export function packagesEmpty(category: CategoryId | undefined): { title: string; description: string } {
  const title = toolTitle('planes', category);
  const { one, feminine } = noun(title);
  const example = category ? `: ${FIRST_PACKAGE[category]}` : '';
  return {
    title: `Todavía no tienes ${title.toLowerCase()}.`,
    description: `Crea tu ${feminine ? 'primera' : 'primer'} ${one}${example}. Lo vendes desde la ficha del cliente.`,
  };
}

/** The floor page in the trade's words: Mesas, Salas, Estaciones, Cabinas, Consultorios, Bahías. */
export function floorCopy(category: CategoryId | undefined): { title: string; add: string; added: string; empty: string } {
  const title = toolTitle('espacios', category);
  const { one, feminine } = noun(title);
  return {
    title,
    add: `Agregar ${one}`,
    added: `${capitalized(one)} ${feminine ? 'agregada' : 'agregado'}`,
    empty: `Todavía no hay ${title.toLowerCase()}.`,
  };
}

/** What one use of a package is called: a class at a studio, a session at a consultorio, a visit elsewhere. */
function visitsWord(title: string): string {
  if (title === 'Planes') return 'Clases';
  if (title === 'Membresías') return 'Sesiones';
  return 'Visitas';
}

/** The package form in the trade's words: "Crear paquete", "Visitas", "Cuántas trae la membresía". */
export function packageWords(category: CategoryId | undefined): { create: string; visits: string; pack: string; visitsHint: string } {
  const title = toolTitle('planes', category);
  const { one, feminine } = noun(title);
  const visits = visitsWord(title);
  return {
    create: `Crear ${one}`,
    visits,
    pack: `Paquete de ${visits.toLowerCase()}`,
    visitsHint: `Cuántas trae ${feminine ? 'la' : 'el'} ${one}`,
  };
}
